import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, PhoneCall } from "lucide-react";
import { RINGS } from "@/lib/relationsData";

const INK = "#33251A";
const C = 170;

export default function PeopleMap({ people, onAdd, onMove, onRemove }) {
  const [name, setName] = useState("");
  const [ring, setRing] = useState("drift");
  const [sel, setSel] = useState(null);
  const list = people || [];

  const add = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({ id: `${Date.now()}`, name: name.trim().slice(0, 18), ring });
    setName("");
  };

  const placed = list.map((p, i) => {
    const r = RINGS.find((x) => x.k === p.ring) || RINGS[1];
    const same = list.filter((x) => x.ring === p.ring);
    const idx = same.findIndex((x) => x.id === p.id);
    const a = ((idx + 0.5) / Math.max(same.length, 1)) * Math.PI * 2 - Math.PI / 2;
    return { ...p, x: C + r.r * Math.cos(a), y: C + r.r * Math.sin(a), tone: r.tone };
  });
  const selected = placed.find((p) => p.id === sel);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="people-map">
      <div className="lg:col-span-6 flex justify-center relative">
        <svg viewBox="0 0 340 340" className="w-full max-w-[420px]">
          {RINGS.map((r) => (
            <circle key={r.k} cx={C} cy={C} r={r.r} fill="none" stroke={r.tone} strokeOpacity="0.28" strokeWidth="1" strokeDasharray="3 5" />
          ))}
          <circle cx={C} cy={C} r="26" fill="#33251A" />
          <text x={C} y={C + 5} textAnchor="middle" fontSize="12" fill="#E89A4F" fontFamily="Outfit, sans-serif">you</text>
          {/* all threads first, so they always sit beneath the people */}
          <g>
            {placed.map((p) => (
              <motion.line key={`t-${p.id}`} x1={C} y1={C} x2={p.x} y2={p.y} stroke={p.tone} strokeOpacity={sel === p.id ? 0.7 : 0.2} strokeWidth="1"
                animate={{ x2: p.x, y2: p.y }} transition={{ duration: 0.6 }} />
            ))}
          </g>
          {placed.map((p, i) => (
            <motion.g key={p.id} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1, x: 0, y: 0 }} transition={{ type: "spring", damping: 15, delay: i * 0.04 }}
              onClick={() => setSel(sel === p.id ? null : p.id)} style={{ cursor: "pointer" }}>
              <motion.circle cx={p.x} cy={p.y} r={sel === p.id ? 18 : 15} fill={p.tone} fillOpacity={sel === p.id ? 1 : 0.85}
                animate={{ cx: p.x, cy: p.y }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} />
              <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="9.5" fill="#fff" fontFamily="Outfit, sans-serif">{p.name.slice(0, 8)}</text>
            </motion.g>
          ))}
        </svg>
      </div>

      <div className="lg:col-span-6">
        <form onSubmit={add} className="flex flex-wrap items-center gap-2.5">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="A name, or just an initial"
            className="flex-1 min-w-[170px] rounded-full px-5 py-3 text-[14.5px] outline-none" style={{ background: "#fff", border: "1px solid rgba(51,37,26,0.12)", color: INK }} data-testid="people-name" />
          <button type="submit" className="rounded-full px-5 py-3 text-[13px] font-semibold text-white inline-flex items-center gap-2" style={{ background: "#B56A22" }} data-testid="people-add">
            <Plus size={13} /> Place them
          </button>
        </form>
        <div className="flex flex-wrap gap-2 mt-3">
          {RINGS.map((r) => (
            <button key={r.k} onClick={() => setRing(r.k)} className="rounded-full px-4 py-2 text-[12px] inline-flex items-center gap-2"
              style={ring === r.k ? { background: r.tone, color: "#fff" } : { background: "rgba(51,37,26,0.05)", color: "rgba(51,37,26,0.6)" }} data-testid={`people-ring-${r.k}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: ring === r.k ? "#fff" : r.tone }} />{r.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-2">
          {RINGS.map((r) => (
            <div key={r.k} className="rounded-2xl px-5 py-4" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(51,37,26,0.07)" }}>
              <div className="flex items-center gap-2.5">
                <span className="w-2 h-2 rounded-full" style={{ background: r.tone }} />
                <p className="font-display text-[16.5px]" style={{ color: INK }}>{r.label}</p>
                <p className="ml-auto text-[12.5px] tabular-nums" style={{ color: "rgba(51,37,26,0.4)" }}>{list.filter((p) => p.ring === r.k).length}</p>
              </div>
              <p className="text-[12.5px] mt-1" style={{ color: "rgba(51,37,26,0.5)" }}>{r.say}</p>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {selected ? (
            <motion.div key={selected.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-5 rounded-[1.4rem] p-5" style={{ background: "#fff", border: `1px solid ${selected.tone}55` }} data-testid="people-selected">
              <div className="flex items-center gap-3">
                <p className="font-display text-[20px] flex-1" style={{ color: INK }}>{selected.name}</p>
                <button onClick={() => { onRemove(selected.id); setSel(null); }} className="text-[11px] inline-flex items-center gap-1" style={{ color: "rgba(51,37,26,0.4)" }} data-testid="people-remove">
                  <X size={11} /> remove
                </button>
              </div>
              <p className="text-[13px] mt-1.5" style={{ color: "rgba(51,37,26,0.5)" }}>Move them, or mark that you actually spoke.</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {RINGS.map((r) => (
                  <button key={r.k} onClick={() => onMove(selected.id, r.k)} className="rounded-full px-3.5 py-2 text-[12px]"
                    style={selected.ring === r.k ? { background: r.tone, color: "#fff" } : { background: "rgba(51,37,26,0.05)", color: "rgba(51,37,26,0.6)" }}
                    data-testid={`people-move-${r.k}`}>{r.label}</button>
                ))}
                <button onClick={() => onMove(selected.id, "close")} className="rounded-full px-3.5 py-2 text-[12px] inline-flex items-center gap-1.5 text-white" style={{ background: "#33251A" }} data-testid="people-spoke">
                  <PhoneCall size={11} /> we spoke
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.p key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-5 text-[13px]" style={{ color: "rgba(51,37,26,0.42)" }}>
              {list.length ? "Tap anybody on the map to move them, or to mark that you spoke." : "Add four or five people and the drift becomes visible in about ten seconds."}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
